import { pickSplat, type FileAccess, type HallResponse } from '@mawkeb/shared/manifest';
import { useEffect, useRef } from 'react';
import { renewAccess } from '../api';
import { HallFiles, wantsLightScan } from '../engine/hallFiles';
import { detectTier, readDevice } from '../engine/quality';
import { useVenue, venue } from '../state/store';
import type { Viewer, ViewState } from '../engine/Viewer';
import type { Tier } from '../engine/quality';

let current: Viewer | null = null;
let engine: Promise<typeof import('../engine/Viewer')> | null = null;

/** The 3D engine's code (three.js and Spark), fetched once; main.tsx starts this at page load. */
export function loadEngine() {
  engine ??= import('../engine/Viewer');
  return engine;
}

/** Resolves once the scan's first view is on screen (or the hall is fully in). */
function firstView(): Promise<void> {
  const seen = () => venue().load.timings.coarse !== undefined || venue().load.phase === 'ready';
  return new Promise((resolve) => {
    if (seen()) return resolve();
    const stop = useVenue.subscribe(() => {
      if (!seen()) return;
      stop();
      resolve();
    });
  });
}

/** The live viewer, for the buttons that act on it (screenshot, reset). */
export function viewer(): Viewer | null {
  return current;
}

export function Stage({ hall, renew }: { hall: HallResponse | null; renew?: () => Promise<FileAccess> }) {
  const host = useRef<HTMLDivElement>(null);
  const view = useVenue((s) => s.view);
  const tool = useVenue((s) => s.tool);

  useEffect(() => {
    if (!hall || !host.current) return;
    const el = host.current;
    let disposed = false;
    let instance: Viewer | null = null;
    let restoreTimer: ReturnType<typeof setTimeout> | null = null;
    const unsubs: (() => void)[] = [];

    // Lighter phones and Data Saver get the lighter copy of the scan. Its header and coarsest chunk
    // start downloading now, alongside the 3D engine.
    const tier = detectTier(venue().device ?? readDevice(null));
    const asked = new URLSearchParams(location.search).get('scan');
    const splat = pickSplat(hall.manifest, asked ? asked === 'light' : wantsLightScan(tier.name));
    const files = splat
      ? new HallFiles(splat, hall.access, renew ?? (() => renewAccess(hall.manifest.hall.id)), {
          onFile: (file, fromDevice) => {
            const l = venue().load;
            venue().setLoad({ bytes: l.bytes + file.bytes, fromDevice: l.fromDevice + (fromDevice ? file.bytes : 0) });
            instance?.requestRender();
          },
          onTrouble: (message) => venue().setLoad({ message: message ?? undefined }),
        })
      : null;
    if (files && splat) {
      void files.get(splat.root).catch(() => {});
      if (splat.chunks[0]) void files.get(splat.chunks[0]).catch(() => {});
    }

    const start = async (tier?: Tier, state?: ViewState) => {
      const { Viewer: ViewerClass } = await loadEngine();
      if (disposed) return;
      try {
        instance = new ViewerClass(el, { onContextLost: lost, onContextRestored: rebuild }, tier);
      } catch {
        venue().setLoad({ phase: 'error', message: 'This device couldn’t start 3D drawing. Close other apps and tabs, then reload.' });
        return;
      }
      current = instance;
      venue().mark('engine');
      instance.setInsets(venue().insets);
      await instance.load(hall, files);
      if (state) instance.importState(state);
      void pictures(instance);
    };

    /**
     * The library's pictures, drawn once per visit when the props' textures are in. The textures wait for
     * the first view of the scan: on a slow connection they would hold up its first chunk.
     */
    const pictures = async (v: Viewer) => {
      await firstView();
      if (disposed || current !== v) return;
      v.propMaterials.prewarm();
      if (Object.keys(venue().thumbs).length) return;
      await v.propMaterials.ready();
      if (disposed || current !== v) return;
      const [{ CATALOGUE }, { renderThumbnails }] = await Promise.all([import('@mawkeb/shared/catalogue'), import('../engine/props/thumbs')]);
      const thumbs = await renderThumbnails(v.renderer, v.propMaterials, CATALOGUE, 128);
      venue().set({ thumbs: Object.fromEntries(thumbs) });
      v.requestRender();
    };

    const lost = () => {
      venue().set({ contextLost: true });
      // Rebuild when the browser gives the context back, or after a moment if it doesn't say.
      restoreTimer = setTimeout(rebuild, 2500);
    };

    const rebuild = () => {
      if (restoreTimer) clearTimeout(restoreTimer);
      restoreTimer = null;
      if (!instance || disposed) return;
      const state = instance.exportState();
      const tier = instance.lighterTier();
      instance.dispose();
      instance = null;
      current = null;
      venue().set({ contextLost: false });
      void start(tier, state);
      venue().toast('The view was reset to free up memory. Everything is where you left it.');
    };

    void start();

    unsubs.push(
      useVenue.subscribe((s, prev) => {
        const v = instance;
        if (!v) return;
        if (s.view !== prev.view) v.setView(s.view);
        if (s.look !== prev.look) v.setLook(s.look);
        if (s.theme !== prev.theme) v.applyTheme();
        if (s.units !== prev.units) v.applyUnits();
        if (s.tool !== prev.tool) v.setTool(s.tool);
        if (s.measurements !== prev.measurements || s.pendingTape !== prev.pendingTape || s.tapeStraight !== prev.tapeStraight) v.setMeasurements(s.measurements);
        if (s.clean !== prev.clean) v.setClean(s.clean);
        if (s.eyeHeight !== prev.eyeHeight) v.setEyeHeight(s.eyeHeight);
        if (s.insets !== prev.insets) v.setInsets(s.insets);
      }),
    );

    return () => {
      disposed = true;
      if (restoreTimer) clearTimeout(restoreTimer);
      unsubs.forEach((u) => u());
      instance?.dispose();
      current = null;
    };
  }, [hall]);

  return <div className="stage" ref={host} data-view={view} data-tool={tool} />;
}
